import type { Guide } from "./types.js";
import { Lead, H2, P, Ol, Ul, Li, AppLink, C, Callout, Step } from "./primitives.js";

export const meta = {
  id: "produce-article",
  title: "From keyword to published article",
  category: "Workflows",
  lastUpdated: "2026-07-11",
  description: "The full pipeline: enrich → scrape → brief → produce → distribute. ~$0.50 + 5-10 min per article.",
  relatedGuides: ["getting-started", "distribute-article", "content-db", "asset-storage", "publish-sanity", "regenerate-article"],
  readTimeMin: 8,
} as const;

export function Body() {
  return (
    <>
      <Lead>
        Every produced article passes through five stages. Each stage is a single button on the keyword's detail panel, and each one logs its result so you can always pick up where the previous run left off.
      </Lead>

      <Callout kind="tip">
        Want to test the flow first? Pick any keyword in <AppLink to="pipeline">Pipeline</AppLink> (or hit "Add keyword") and walk it through to <strong>Produce</strong>. You can do everything except actual distribution without spending more than ~$0.50.
      </Callout>

      <H2>The five stages</H2>

      <Step n={1} title="Enrich — SerpAPI snapshot">
        <P>
          <strong>What it does</strong>: hits SerpAPI for the keyword in Hebrew (il / iw locale), captures top 10 organic results, People Also Ask questions, related searches, query fanout variants, AI Overview presence.
        </P>
        <P>
          <strong>Where to click</strong>: Pipeline → keyword row → detail panel → <C>Enrich</C> button.
        </P>
        <P>
          <strong>Outcome</strong>: keyword status flips <C>backlog → researching</C>. The SERP tab in the detail panel becomes populated.
        </P>
        <P>~5-15s, ~$0.001 per call.</P>
      </Step>

      <Step n={2} title="Scrape competitors — Firecrawl">
        <P>
          <strong>What it does</strong>: for the top 5-10 competitor URLs from SerpAPI (auto-filtered to skip WAF-blocked HMO portals), fetches markdown + headings + JSON-LD + alt texts via Firecrawl.
        </P>
        <P>
          <strong>Where to click</strong>: <C>Scrape competitors</C> button (only enabled after Enrich).
        </P>
        <P>
          <strong>Outcome</strong>: each successful scrape lands as a row in the <strong>Extracts</strong> tab. Failed scrapes are logged with the reason — typically WAF or 404.
        </P>
        <P>~30-60s, ~$0.005 per article (Firecrawl).</P>
      </Step>

      <Step n={3} title="Brief — Claude synthesises a winning outline">
        <P>
          <strong>What it does</strong>: Claude reads the SerpAPI + competitor extracts and produces the §5.2 brief — outline H1, 5-9 H2/H3 sections with intent + mustCoverPoints + targetWordCount, FAQ section, semanticMustCover entities, competitiveGap, treatmentBenefitTriple, uniquePerspective, israelContext, sources, hashtag-ready topics.
        </P>
        <P>
          <strong>Heads up — primary/secondary keywords</strong>: if this keyword has <C>primaryKeywordId</C> set (i.e. it's a secondary from cannibalization grouping), the brief route refuses with a tooltip pointing at the primary. Always brief the primary; secondaries get folded automatically.
        </P>
        <P>
          <strong>SEO meta (H1 + meta title + description)</strong>: the brief also produces a SERP-grounded, <strong>editable</strong> H1, meta title, and meta description, sized to the limits that matter — H1 ≤60, meta title ≤47 (the site appends " | AlternaBe" = 13 chars, so the rendered SERP title stays ≤60), description 140-160. Edit any field inline in the Brief tab, or hit <strong>Regenerate from SERP</strong> to get a fresh set grounded in competitor titles/H1s + PAA + fanout. Every generation is kept as a <strong>variation</strong> you can review and re-select. Production prefers whatever meta you reviewed here.
        </P>
        <P>
          <strong>Where to click</strong>: <C>Generate brief</C> button.
        </P>
        <P>
          <strong>Outcome</strong>: status → <C>briefed</C>. Brief tab populated. Internal links resolved (cluster + topic-overlap match against the Content DB live mirror).
        </P>
        <P>~20-40s, ~$0.05 on Sonnet 4.6.</P>
      </Step>

      <Step n={4} title="Produce — Heading-based article generation with caching">
        <P>
          <strong>What it does</strong>: Claude writes the article section-by-section. Each H2 is a separate API call with a running summary of all prior sections injected as context. The system prompt is cached (verified ~21K input tokens read across an 11-section run), so the per-section cost stays low.
        </P>
        <P>
          <strong>What the writer sees</strong>: the production prompt surfaces the full brief — semantic must-cover entities, competitive gap, unique perspective, Israel context, brand tie-ins, internal-link candidates (from Content DB cluster matches), authoritative sources, the outline overview, and per-section must-cover points with intent + H3 sub-headings + target word count.
        </P>
        <P>
          <strong>Secondary keywords (verbatim-required)</strong>: when this keyword has cannibalization-grouped secondaries folded into it, those phrases land in a SEPARATE block above semanticMustCover with strict rules. Claude must include each phrase exactly (or as a very close variant) at least once, distribute them across sections, and never paraphrase. This is what makes the produced article rank for the WHOLE group of search queries, not just the head term. Updated 2026-05-24.
        </P>
        <P>
          <strong>Humanization rules</strong> (2026-05-24): the brand voice prompt now bans em dashes (the #1 AI-detection signal) along with a list of Hebrew + English phrases that scream "AI wrote this" (חשוב לציין / נצלול לעומק / delve into / tapestry / landscape / etc). It also instructs Claude to vary sentence rhythm, address the reader directly, name specific cities and brands instead of generic claims, and avoid textbook section structures. These rules apply only to the article body, not the operator-facing meta.
        </P>
        <P>
          <strong>Where to click</strong>: <C>Produce article</C>. Shows live per-section progress via SSE — you'll see each section land with token counts + word counts.
        </P>
        <P>
          <strong>Outcome</strong>: status → <C>reviewing</C>. Article tab populated with the full markdown. Token spend + estimated cost surfaced at the end.
        </P>
        <P>~60-90s, ~$0.23 (Sonnet) or ~$0.53 (Opus). Pillars use Opus by default.</P>
      </Step>

      <Step n={5} title="Distribute — fire-and-forget across 8 channels">
        <P>
          <strong>What it does</strong>: pushes the article (+ produced video + hero image where applicable) to whichever channel you pick. Each channel is a separate dropdown entry — usually with a dry-run option to preview before going live.
        </P>
        <P>
          See the <AppLink to="help?guide=distribute-article">Distributing content</AppLink> guide for per-channel walkthroughs.
        </P>
      </Step>

      <H2>Post-production actions (UI buttons)</H2>
      <P>
        After Produce finishes, four buttons unlock on the Pipeline detail panel's action toolbar (below the main Enrich / Brief / Produce / Distribute row):
      </P>
      <Ul>
        <Li><strong>Voiceover</strong> — ElevenLabs renders a 90-120s Hebrew narration MP3. ~$0.27 + 15s. Auto-uploads to Sanity + Drive.</Li>
        <Li><strong>Storyboard</strong> — Sonnet 4.6 produces a 5-10 scene JSON plan for the viral video. ~$0.05 + 15s. The button label flips to <C>Storyboard ✓</C> once one exists; re-clicking regenerates.</Li>
        <Li><strong>Viral video</strong> — disabled until a storyboard exists. Chains scene images → scene videos → per-scene voiceovers → Remotion composer → final 9:16 MP4. ~$1.10 + 5 min. Auto-uploads as <C>viral_final</C>.</Li>
        <Li><strong>Publish to Sanity</strong> — markdown → Portable Text → <C>blog-posts</C> draft. ~5s. Returns a Sanity Studio URL.</Li>
      </Ul>
      <P>
        See the <strong>Assets</strong> tab in the same panel for a live view of which files have been uploaded to Sanity + Drive, with status pills (uploaded / partial / failed) + clickable URLs. CLI equivalents (<C>keywords voiceover generate</C>, <C>keywords storyboard generate</C>, <C>keywords viral generate</C>, <C>keywords publish</C>) still work for scripted runs.
      </P>

      <H2>Optional intermediate steps</H2>
      <Ul>
        <Li>
          <strong>Images + Video generation</strong> — between Produce and Distribute, run <C>keywords images generate &lt;id&gt;</C> + <C>keywords videos generate &lt;id&gt;</C> from the CLI to create the assets. Needed for FB photo posts, IG posts, TikTok, YouTube. ~$0.10-0.30 per article on Replicate. <strong>Each generated file uploads automatically to Sanity (public URL for distribution) + Google Drive (team browsing)</strong> — see <AppLink to="help?guide=asset-storage">Asset storage</AppLink>. IG / FB pick their image URL from this table, no paste needed.
        </Li>
        <Li>
          <strong>Refresh</strong> — re-run SerpAPI for an existing article to diff PAA / top-3 / AI Overview state. Recommends <C>no-op</C>, <C>review-prose</C>, or <C>regenerate-article</C>. Stamps <C>lastReviewedAt</C> so Google sees fresh content.
        </Li>
        <Li>
          <strong>Regenerate</strong> — for a live article that dropped in ranking or needs updating, the <C>Regenerate</C> button runs the whole pipeline again (SERP → meta → body → images → re-publish) with operator gates, and preserves the original publish date. See <AppLink to="help?guide=regenerate-article">Refreshing &amp; regenerating a published article</AppLink>.
        </Li>
      </Ul>

      <H2>What to do when something breaks</H2>
      <Ol>
        <Li><strong>Enrich fails</strong> → SerpAPI quota or auth issue. Check Credentials → SerpAPI → Test.</Li>
        <Li><strong>Scrape returns 0 extracts</strong> → all top competitors were WAF-blocked. Pass <C>--include-blocked</C> via CLI to override, or use a different keyword.</Li>
        <Li><strong>Brief fails</strong> → usually Anthropic balance or a malformed extract. Re-run; the brief generator is idempotent.</Li>
        <Li><strong>Produce stalls past 5 min</strong> → SSE connection may have dropped; the keyword is still being written server-side. Check the Articles page in 2 min.</Li>
        <Li><strong>Distribute fails on a specific channel</strong> → click that channel's Credentials card → Test to isolate.</Li>
      </Ol>
    </>
  );
}

export const guide: Guide = { meta, Body };
